import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Grid,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import BaseDialog from '../../BaseDialog'
import SaveQuickFilterDialog from '../SaveQuickFilterDialog'
import { hasPermission, useUserState } from '../../../context/user/UserContext'
import { Permissions } from '../../../entities/permissions'
import { useNotificationsProvider } from '../../../context/NotificationsContext'

export default function ManageQuickFiltersDialog({
  open,
  onClose,
  publicFilters,
  privateFilters,
  defaultFilter,
  onSetAsDefault,
  onClearDefault,
  onUpdateFilter,
  onDeleteFilter,
}) {
  const { t } = useTranslation()
  const { user } = useUserState()
  const { showNotification } = useNotificationsProvider()

  const canManagePublic = hasPermission(
    Permissions.PAGE_QUICK_FILTERS_MANAGE_PUBLIC,
    user
  )

  const [editing, setEditing] = useState(null)
  const [confirmingDelete, setConfirmingDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const renderRow = (filter) => {
    const isOwner = filter.creatorUserId === user?.id
    const canEdit = filter.isPublic ? canManagePublic : isOwner
    const isDefault = defaultFilter?.id === filter.id

    return (
      <Box
        key={filter.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          py: 1,
          mb: 0.5,
          borderRadius: 1,
          backgroundColor: 'action.hover',
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: 'action.selected' },
          '&:last-of-type': { mb: 0 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" noWrap>
            {filter.name}
          </Typography>
          {filter.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {filter.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip
            title={
              isDefault
                ? t('quick-filters.clear-default')
                : t('quick-filters.set-as-default')
            }
          >
            <IconButton
              size="small"
              onClick={async () => {
                try {
                  if (isDefault) {
                    await onClearDefault()
                  } else {
                    await onSetAsDefault(filter.id)
                  }
                } catch (err) {
                  showNotification('error', t('errors.api-error'))
                }
              }}
            >
              {isDefault ? (
                <StarIcon fontSize="small" color="warning" />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Tooltip title={t('quick-filters.edit-action')}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setEditing(filter)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title={t('quick-filters.delete-action')}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setConfirmingDelete(filter)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        hideActions
        title={t('quick-filters.manage-title')}
      >
        <Stack spacing={2} sx={{ pt: 2 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('quick-filters.public-section')}
            </Typography>
            {publicFilters.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('quick-filters.no-public')}
              </Typography>
            ) : (
              publicFilters.map(renderRow)
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('quick-filters.private-section')}
            </Typography>
            {privateFilters.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('quick-filters.no-private')}
              </Typography>
            ) : (
              privateFilters.map(renderRow)
            )}
          </Box>
        </Stack>
      </BaseDialog>

      <SaveQuickFilterDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        initialValues={editing || undefined}
        onSave={async ({ name, description, isPublic }) => {
          await onUpdateFilter(editing.id, {
            name,
            description,
            isPublic,
          })
        }}
      />

      <BaseDialog
        open={!!confirmingDelete}
        onClose={() => {
          if (!deleting) setConfirmingDelete(null)
        }}
        onConfirm={async () => {
          if (!confirmingDelete) return
          setDeleting(true)
          try {
            await onDeleteFilter(confirmingDelete.id)
            showNotification('success', t('quick-filters.delete-success'))
            setConfirmingDelete(null)
          } catch (err) {
            showNotification('error', t('errors.api-error'))
          } finally {
            setDeleting(false)
          }
        }}
        title={t('quick-filters.delete-title')}
        loading={deleting}
      >
        <Grid container direction="column" spacing={2} sx={{ pt: 2 }}>
          <Grid item>
            <Typography>
              {t('quick-filters.delete-confirm-message', {
                name: confirmingDelete?.name || '',
              })}
            </Typography>
          </Grid>
        </Grid>
      </BaseDialog>
    </>
  )
}
